/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useState, useEffect } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";

// Soft UI Dashboard PRO React components
import SoftBox from "../../../../components/SoftBox";
import SoftTypography from "../../../../components/SoftTypography";

// Soft UI Dashboard PRO React base styles
import borders from "../../../../assets/theme/base/borders";
import colors from "../../../../assets/theme/base/colors";
import { UseYetiStats } from "../../../../hooks/company";
import { formatNumber } from "../../../../helpers/formatter";
import Tooltip from "@mui/material/Tooltip";


function ReportsDoughnutChartItem({
  color,
  title,
  id,
  type,
  normalValue,
  pointsValue,
  item,
  percentage,
  hasBorder,
  isYeti,
}) {
  const { borderWidth } = borders;
  const { light } = colors;
  const [plus, setPlus] = React.useState("");
  const [yetiDiff, setYetiDiff] = useState(0);
  const yeti = UseYetiStats();
  const [isLeading, setIsLeading] = React.useState(true);

  useEffect(() => {
    if (yeti) {
      const versusYeti = pointsValue - yeti.month[id]?.pts;
      setPlus(versusYeti > 0 ? "+" : "");
      setIsLeading(versusYeti >= 0);

      // Original value
      let yetiDifference = normalValue - yeti.month[id]?.amt;

      // Since we append the "plus" sign if the "versusYeti" (points) is greater than 0,
      // let this sign take precedence over the negative "yetiDifference" (amount) value
      // Note: This behaviour only happens in Negative direction anchor metrics
      if (
        type === "anchor" &&
        versusYeti > 0 &&
        yetiDifference < 0
      ) {
        // Make the yetiDifference positive to remove its negative (-) sign
        yetiDifference *= -1;
      }

      setYetiDiff(yetiDifference);
    }
  }, [type, yeti, normalValue, pointsValue, id]);

  return (
    <Grid
      container
      justifyContent="space-between"
      alignItems="center"
      sx={{
        borderBottom: hasBorder
          ? `${borderWidth[1]} solid ${light.main}`
          : "none",
        lineHeight: 1.25,
      }}
    >
      <Grid item mini={9}>
        <SoftBox display="flex" py={0.8} px={1}>
          <SoftBox
            width="1.35rem"
            height="1.35rem"
            borderRadius="md"
            mr={1}
            lineHeight={0}
            sx={{ background: color }}
          />
          <SoftBox
            display="flex"
            flexDirection="flex-start"
            justifyContent="center"
          >
            <SoftTypography
              component="div"
              variant="button"
              textTransform="capitalize"
              fontWeight="medium"
            >
              {title}
            </SoftTypography>
            {isLeading && item !== 0 && isYeti && (
              <SoftBox sx={{marginLeft: '0.5rem!important'}}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_105_18)">
                  <circle cx="12" cy="12" r="12" fill="#FFF8CB"/>
                  <path d="M6.75001 18.75H17.25M11.6715 5.4495C11.7039 5.3907 11.7514 5.34167 11.8092 5.30752C11.867 5.27338 11.9329 5.25536 12 5.25536C12.0671 5.25536 12.133 5.27338 12.1908 5.30752C12.2486 5.34167 12.2961 5.3907 12.3285 5.4495L14.5425 9.6525C14.5953 9.74982 14.669 9.83424 14.7583 9.89971C14.8476 9.96518 14.9503 10.0101 15.059 10.0311C15.1677 10.0522 15.2797 10.049 15.387 10.0216C15.4943 9.99429 15.5942 9.94353 15.6795 9.873L18.8873 7.125C18.9488 7.07491 19.0247 7.04566 19.104 7.04145C19.1832 7.03724 19.2618 7.05828 19.3283 7.10156C19.3949 7.14483 19.446 7.2081 19.4743 7.28226C19.5025 7.35643 19.5066 7.43765 19.4858 7.51425L17.3603 15.1987C17.3169 15.356 17.2234 15.4948 17.094 15.5942C16.9646 15.6935 16.8064 15.7479 16.6433 15.7492H7.35751C7.19426 15.7481 7.03585 15.6937 6.90631 15.5944C6.77677 15.495 6.68318 15.3561 6.63976 15.1987L4.51501 7.515C4.4942 7.4384 4.49823 7.35718 4.52652 7.28301C4.55481 7.20885 4.6059 7.14558 4.67244 7.10231C4.73898 7.05903 4.81753 7.03799 4.89679 7.0422C4.97605 7.04641 5.05194 7.07566 5.11351 7.12575L8.32051 9.87375C8.40586 9.94428 8.50577 9.99504 8.61306 10.0224C8.72035 10.0497 8.83237 10.053 8.94106 10.0319C9.04976 10.0108 9.15244 9.96593 9.24173 9.90046C9.33102 9.83499 9.40472 9.75057 9.45751 9.65325L11.6715 5.4495Z" stroke="#FDC500" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                  </g>
                  <defs>
                  <clipPath id="clip0_105_18">
                  <rect width="24" height="24" fill="white"/>
                  </clipPath>
                  </defs>
                </svg>
              </SoftBox>
            )}
          </SoftBox>
        </SoftBox>
      </Grid>
      <Grid item mini={3}>
        <SoftBox
          py={0.8}
          px={1}
          textAlign="center"
          width="100%"
          display="flex"
          justifyContent="end"
        >
          <SoftTypography
            variant="caption"
            color="text"
            fontWeight="medium"
            sx={{ display: "inline-block", width: "max-content" }}
          >
            {item || "0"}
          </SoftTypography>
          {yetiDiff > 0 && isYeti && (
            <Tooltip title={`${formatNumber(Math.round(yetiDiff), type)} above the Yeti`} placement="top" arrow>
              <SoftBox display="flex" alignItems="center" sx={{marginLeft: '0.5rem!important', background: "rgba(212, 240, 236, 0.5)!important", padding: '2px 6px 0 6px!important', borderRadius: '4px!important', marginTop: '-0.25rem!important'}}>
                <svg width="14" height="12" viewBox="0 0 14 12" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginTop: "3px"}}>
                  <path d="M7.52917 3.43998C7.45 3.43998 7.37083 3.41665 7.30833 3.36665L5 1.51998L2.69167 3.36665C2.57083 3.46331 2.37083 3.46331 2.25 3.36665C2.12917 3.26998 2.12917 3.10998 2.25 3.01331L4.77917 0.98998C4.9 0.893314 5.1 0.893314 5.22083 0.98998L7.75 3.01331C7.87083 3.10998 7.87083 3.26998 7.75 3.36665C7.69167 3.41665 7.60833 3.43998 7.52917 3.43998Z" fill="#125F05"/>
                  <path d="M5 7.08339C4.82917 7.08339 4.6875 6.97006 4.6875 6.83339V1.22339C4.6875 1.08672 4.82917 0.973389 5 0.973389C5.17083 0.973389 5.3125 1.08672 5.3125 1.22339V6.83339C5.3125 6.97006 5.17083 7.08339 5 7.08339Z" fill="#125F05"/>
                </svg>
                <SoftTypography
                  variant="caption"
                  color="text"
                  fontWeight="medium"
                  sx={{ display: "inline-block", width: "max-content", color: "#125F05" }}
                >
                  {formatNumber(Math.round(yetiDiff), type)}
                </SoftTypography>
              </SoftBox>
            </Tooltip>
          )}
          {yetiDiff < 0 && isYeti && (
            <Tooltip title={`${formatNumber(Math.round(Math.abs(yetiDiff)), type)} below the Yeti`} placement="top" arrow>
              <SoftBox display="flex" alignItems="center" sx={{marginLeft: '0.5rem!important', background: "rgba(245, 216, 220, 0.5);!important", padding: '2px 6px 0 6px!important', borderRadius: '4px!important', marginTop: '-0.25rem!important'}}>
                <svg width="14" height="12" viewBox="0 0 14 12" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginTop: "3px"}}>
                  <path d="M8.02917 4.56002C7.95 4.56002 7.87083 4.58335 7.80833 4.63335L5.5 6.48002L3.19167 4.63335C3.07083 4.53669 2.87083 4.53669 2.75 4.63335C2.62917 4.73002 2.62917 4.89002 2.75 4.98669L5.27917 7.01002C5.4 7.10669 5.6 7.10669 5.72083 7.01002L8.25 4.98669C8.37083 4.89002 8.37083 4.73002 8.25 4.63335C8.19167 4.58335 8.10833 4.56002 8.02917 4.56002Z" fill="#E5484D"/>
                  <path d="M5.5 0.916611C5.32917 0.916611 5.1875 1.02994 5.1875 1.16661L5.1875 6.77661C5.1875 6.91328 5.32917 7.02661 5.5 7.02661C5.67083 7.02661 5.8125 6.91328 5.8125 6.77661L5.8125 1.16661C5.8125 1.02994 5.67083 0.916611 5.5 0.916611Z" fill="#E5484D"/>
                </svg>
                <SoftTypography
                  variant="caption"
                  color="text"
                  fontWeight="medium"
                  sx={{ display: "inline-block", width: "max-content", color: "#E5484D" }}
                >
                  {formatNumber(Math.round(Math.abs(yetiDiff)), type)}
                </SoftTypography>
              </SoftBox>
            </Tooltip>
          )}
        </SoftBox>
      </Grid>
    </Grid>
  );
}

// Setting default values for the props of ReportsDoughnutChartItem
ReportsDoughnutChartItem.defaultProps = {
  color: "info",
  hasBorder: false,
};

// Typechecking props for the ReportsDoughnutChartItem
ReportsDoughnutChartItem.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
  ]),
  title: PropTypes.string.isRequired,
  hasBorder: PropTypes.bool,
};

export default ReportsDoughnutChartItem;
